import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import {
   getPipelineIdFromURL,
   isEnableOpenSearchForGlobalSearch,
   useCRMItemUIStore
} from '@flowwai-frontend/bps-officer-web/common/ui'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import {
   API_SUCCESS,
   APIStatus,
   getLoadingStatus
} from '@flowwai-frontend/shared/api-common'
import { LoadingWrapper } from '@flowwai-frontend/shared/common-components'
import {
   useGetBaseTemplateIds,
   usePipelineStore
} from '@flowwai-frontend/shared/sales-crm/sales-crm-core'

import { useGetGSFields } from '../../apis/getGSFields/useGetGSFields'
import useSearchPipelineItemsAPIHookWrapper from '../../apis/searchPipelineItemsAPIHookWrapper/useSearchPipelineItemsAPIHookWrapper'
import GlobalSearchHeader from '../../components/GlobalSearchHeader/GlobalSearchHeader'
import GSFieldsSelection from '../../components/GSFieldsSelection/GSFieldsSelection'
import GSPipelinePicker from '../../components/GSPipelinePicker/GSPipelinePicker'
import { useGlobalSearchUIStore } from '../../hooks/stores/useGetGlobalSearchUIStore'
import { GSFieldType } from '../../types'
import GSErrorView from './components/GSErrorView'
import NoDataView from './components/GSNoDataView'
import GSResultsList from './components/GSResultsList'
import { SEARCH_RESULTS_LIMIT } from './constants'
import {
   commonClassNameForLoadingFailureNoDataView,
   containerClassName,
   pipeClassName,
   pipelineAndFieldsSelectionContainerClassName
} from './styles'

const GlobalSearchContentController = () => {
   const pipelineStore = usePipelineStore()

   const location = useLocation()
   const pipelineId = getPipelineIdFromURL(location.pathname)

   const setDefaultPipelineId = () =>
      pipelineId ? pipelineId : pipelineStore.pipelines[0]?.pipelineId ?? ''

   const [searchQuery, setSearchQuery] = useState('')
   const [selectedPipelineId, setSelectedPipelineId] = useState(
      setDefaultPipelineId()
   )

   const { onOpenTableCRMItemDetailsSidePane } = useCRMItemUIStore()
   const globalSearchUIStore = useGlobalSearchUIStore()
   const { closeGlobalSearchModal } = globalSearchUIStore

   const { baseTemplateIds } = useGetBaseTemplateIds(selectedPipelineId)

   const [fields, setFields] = useState<GSFieldType[]>([])
   const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>([])
   const [canSearchOnAllFields, setCanSearchOnAllFields] = useState(true)

   const { theme } = useAppTheme()
   const { fieldSelectionPipeBgColor } = theme.globalSearch.searchModal

   const shouldRenderResults = searchQuery !== ''

   const getFieldsAPI = useGetGSFields()
   const getGSResultsAPI = useSearchPipelineItemsAPIHookWrapper(
      isEnableOpenSearchForGlobalSearch
   )

   const requestObjectForGS = (offset = 0) => ({
      pipelineId: selectedPipelineId,
      searchQuery,
      fieldIds: canSearchOnAllFields ? [] : selectedFieldIds,
      canSearchOnAllFields: canSearchOnAllFields,
      pagination: { limit: SEARCH_RESULTS_LIMIT, offset }
   })

   const getGlobalSearchResults = () => {
      getGSResultsAPI.triggerAPI({
         requestObj: requestObjectForGS()
      })
   }

   const fetchMoreResults = (offset: number) => {
      getGSResultsAPI.fetchMore({ requestObj: requestObjectForGS(offset) })
   }

   const getFields = () => {
      getFieldsAPI.triggerAPI({
         pipelineId: selectedPipelineId,
         salesTemplateIds: baseTemplateIds
      })
   }

   useEffect(() => {
      if (getFieldsAPI.fields.length > 0) {
         setFields(getFieldsAPI.fields)
         setSelectedFieldIds(getFieldsAPI.fields.map(field => field.fieldId))
      } else {
         setFields([])
         setSelectedFieldIds([])
      }
   }, [JSON.stringify(getFieldsAPI.fields)])

   useEffect(() => {
      getFields()
   }, [selectedPipelineId])

   useEffect(() => {
      if (searchQuery === '') return
      getGlobalSearchResults()
   }, [searchQuery, selectedPipelineId, JSON.stringify(selectedFieldIds)])

   const renderFailureView = (): ReactElement => {
      const onRetry = () => {
         if (getFieldsAPI.apiError) {
            getFields()
         } else {
            getGlobalSearchResults()
         }
      }
      return <GSErrorView onRetry={onRetry} />
   }

   const onClickGSResultItem = (pipelineItemId: string) => {
      const pipelineItemType = pipelineStore.pipelines.find(
         pipeline => pipeline.pipelineId === selectedPipelineId
      )?.pipelineItemType

      if (!pipelineItemType) return

      onOpenTableCRMItemDetailsSidePane(
         pipelineItemId,
         pipelineItemType,
         selectedPipelineId,
         undefined,
         true
      )

      closeGlobalSearchModal()
   }

   const renderSuccessView = (): ReactElement => {
      const shouldRenderNoDataView = !(getGSResultsAPI.searchResults.length > 0)

      if (shouldRenderNoDataView) return <NoDataView />
      return (
         <GSResultsList
            isFromTemplateContent={false}
            totalSearchResults={getGSResultsAPI.totalCount ?? 0}
            searchResults={getGSResultsAPI.searchResults}
            fetchMoreResults={fetchMoreResults}
            onClickGSResultItem={onClickGSResultItem}
         />
      )
   }

   const hasMoreResults =
      getGSResultsAPI.totalCount &&
      getGSResultsAPI.totalCount > getGSResultsAPI.searchResults.length

   const getAPIStatus = (): APIStatus =>
      hasMoreResults
         ? API_SUCCESS
         : getLoadingStatus(getFieldsAPI.apiStatus, getGSResultsAPI.apiStatus)

   const shouldShowFields = () => {
      const matchPattern = /^[0-9+ ]+$/
      return matchPattern.test(searchQuery)
   }

   const onChangeSelectedFields = (ids: string[]) => {
      setCanSearchOnAllFields(false)
      setSelectedFieldIds(ids)
   }
   const onChangePipelineId = (pipelineId: string) => {
      setCanSearchOnAllFields(true)
      setSelectedPipelineId(pipelineId)
   }

   return (
      <div className={containerClassName}>
         <GlobalSearchHeader onChangeSearchQuery={setSearchQuery} />
         <div className={pipelineAndFieldsSelectionContainerClassName}>
            <GSPipelinePicker
               selectedPipelineId={selectedPipelineId}
               setSelectedPipelineId={onChangePipelineId}
               pipelines={pipelineStore.pipelines}
            />
            {shouldShowFields() ? (
               <>
                  <div
                     className={`${pipeClassName} ${fieldSelectionPipeBgColor}`}
                  />
                  <GSFieldsSelection
                     fields={fields}
                     selectedFieldIds={selectedFieldIds}
                     setSelectedFieldIds={onChangeSelectedFields}
                     apiStatus={getFieldsAPI.apiStatus}
                     apiError={getFieldsAPI.apiError}
                  />
               </>
            ) : null}
         </div>
         {shouldRenderResults ? (
            <LoadingWrapper
               apiError={getGSResultsAPI.apiError || getFieldsAPI.apiError}
               apiStatus={getAPIStatus()}
               containerClassName={commonClassNameForLoadingFailureNoDataView}
               renderFailureView={renderFailureView}
            >
               {renderSuccessView()}
            </LoadingWrapper>
         ) : null}
      </div>
   )
}

export default observer(GlobalSearchContentController)
